import { query, mutation } from '@/dex-shared/helpers/graphql';
import { graphqlQuery, graphqlMutation, ServicesEnum } from '@/helpers/graphql';

const buildMutationBasedOnStudyPlanningAccess = (
  hasAccessToPostgradStudyPlanning: boolean,
) => {
  if (hasAccessToPostgradStudyPlanning) {
    return `
      mutation allowCourse ($courseId: Int!) {
        allowCourse (courseId: $courseId)
      }
    `;
  }

  return `
    mutation disallowCourse ($courseId: Int!) {
      disallowCourse (courseId: $courseId)
    }
  `;
};

export default {
  state: {
    courses: [],
    course: null,
    paginationInformation: {},
  },
  getters: {
    currentCourse: (state: any) => {
      return state.course;
    },
  },
  mutations: {
    SET_COURSES(state: any, courses: any) {
      state.courses = courses;
    },

    SET_COURSES_PAGINATION_INFORMATION(
      state: any,
      paginationInformation: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
      },
    ) {
      state.paginationInformation = paginationInformation;
    },

    SET_ACCESS_TO_POSTGRAD_STUDY_PLANNING(state: any, hasAccess: boolean) {
      state.course.hasAccessToPostgradStudyPlanning = hasAccess;
    },

    SET_CURRENT_COURSE(state: any, course: any) {
      course.classrooms = course.classroomsByCourseId.nodes;
      course.courseBoards = [];
      if (course.courseBoardsByCourseId) {
        course.courseBoardsByCourseId.nodes.map(courseBoard => {
          const periodExists = course.courseBoards.filter(
            cb => cb.period === courseBoard.suggestedPeriod,
          )[0];

          courseBoard.board = courseBoard.boardByBoardId;
          const board = courseBoard.board;
          board.classrooms =
            board.boardsClassroomsByBoardId.nodes.length > 0
              ? board.boardsClassroomsByBoardId.nodes.map(
                  boardsClassroom => boardsClassroom.classroomByClassroomId,
                )
              : [];

          if (periodExists) {
            periodExists.courseBoards.push(courseBoard);
            return;
          }
          course.courseBoards.push({
            period: courseBoard.suggestedPeriod,
            periodLabel:
              courseBoard.suggestedPeriod === null
                ? 'Sem período'
                : `${courseBoard.suggestedPeriod}° período`,
            courseBoards: [courseBoard],
          });
        });
      }
      state.course = course;
    },

    SET_EMPTY_COURSE(state: any) {
      let course = {
        id: null,
        name: null,
        description: null,
        descomplicaCourseId: null,
      };
      state.course = course;
    },
  },

  actions: {
    async PULL_COURSE({ commit }: any, courseId: number) {
      let response = await query(
        `query getCourseByIdDexAdmin($courseId: Int!) {
          courseById(id: $courseId) {
            id
            name
            slug
            description
            descomplicaCategorySlug
            descomplicaCourseId
            classroomsByCourseId {
              nodes {
                id
                name
                descomplicaCourseSlug
              }
            }
            courseBoardsByCourseId(orderBy: SUGGESTED_PERIOD_ASC){
              nodes{
                id
                description
                suggestedPeriod
                boardByBoardId{
                  id
                  title
                  boardsClassroomsByBoardId(filter: {
                    classroomByClassroomId: {
                      courseId: { equalTo: $courseId }
                    }
                  }) {
                    nodes {
                      id
                      classroomByClassroomId {
                        id
                        name
                        description
                        courseId
                        isBonus
                      }
                    }
                  }
                }
              }
            }
          }
        }`,
        {
          courseId: +courseId,
        },
      );

      let course = response.data.courseById;
      commit('SET_CURRENT_COURSE', course);
    },

    async PULL_ACCESS_TO_POSTGRAD_STUDY_PLANNING({ commit }, courseId: number) {
      const response = await graphqlQuery(
        ServicesEnum.POSTGRAD_STUDY_PLANNING,
        `query getPostgradStudyPlanningAvailabilityForCourse ($courseId: Int!) {
          getPostgradStudyPlanningAvailabilityForCourse(courseId: $courseId)
        }`,
        {
          courseId,
        },
      );

      if (response) {
        commit(
          'SET_ACCESS_TO_POSTGRAD_STUDY_PLANNING',
          response.data.getPostgradStudyPlanningAvailabilityForCourse,
        );
      }
    },

    async PUSH_POSTGRAD_STUDY_PLANNING_ACCESS(
      { commit }: any,
      { course, hasAccessToPostgradStudyPlanning },
    ) {
      const builtMutation = buildMutationBasedOnStudyPlanningAccess(
        hasAccessToPostgradStudyPlanning,
      );
      const accessResponse = await graphqlMutation(
        ServicesEnum.POSTGRAD_STUDY_PLANNING,
        builtMutation,
        {
          courseId: course.id,
        },
      );

      if (accessResponse !== null) {
        commit(
          'SET_ACCESS_TO_POSTGRAD_STUDY_PLANNING',
          hasAccessToPostgradStudyPlanning,
        );
      }
    },

    async PUSH_COURSE({ commit, dispatch }: any, course: any) {
      let response;
      let courseResponse;
      const commonParameters =
        '$name: String!, $description: String!, $descomplicaCategorySlug: String, $descomplicaCourseId: String';

      const alreadyExists = !!course.id;

      if (alreadyExists) {
        //update
        response = await mutation(
          `mutation updateCourse ($courseId: Int!, ${commonParameters}) {
            updateCourseById(
              input:
                {id: $courseId,
                  coursePatch:
                  {
                    name: $name,
                    description: $description,
                    descomplicaCategorySlug: $descomplicaCategorySlug
                    descomplicaCourseId: $descomplicaCourseId
                  }
                }
            )
            {
              course {
                id
                name
                slug
                description
                descomplicaCategorySlug
                descomplicaCourseId
                classroomsByCourseId {
                  nodes {
                    id
                    name
                    descomplicaCourseSlug
                  }
                }
              }
            }
          }`,
          {
            courseId: course.id,
            name: course.name,
            description: course.description,
            descomplicaCategorySlug: course.descomplicaCategorySlug,
            descomplicaCourseId: course.descomplicaCourseId,
          },
        );
        courseResponse = response.data.updateCourseById.course;
      } else {
        //create
        response = await mutation(
          `mutation createCourse (${commonParameters}) {
            createCourse(
              input:
                {course:
                  {
                    name: $name,
                    description: $description,
                    descomplicaCategorySlug: $descomplicaCategorySlug
                    descomplicaCourseId: $descomplicaCourseId
                  }
                }
            )
            {
              course {
                id
                name
                slug
                description
                classroomsByCourseId {
                  nodes {
                    id
                    name
                    description
                    descomplicaCourseSlug
                  }
                }
              }
            }
          }`,
          {
            name: course.name,
            description: course.description,
            descomplicaCategorySlug: course.descomplicaCategorySlug,
            descomplicaCourseId: course.descomplicaCourseId,
          },
        );
        courseResponse = response.data.createCourse.course;
      }

      if (courseResponse !== null) {
        commit('SET_CURRENT_COURSE', courseResponse);
      }

      await dispatch('PUSH_POSTGRAD_STUDY_PLANNING_ACCESS', {
        course: courseResponse || course,
        hasAccessToPostgradStudyPlanning:
          course.hasAccessToPostgradStudyPlanning,
      });
    },

    async PULL_COURSES(
      { commit }: any,
      {
        currentPage,
        numberOfCoursesToShow,
        searchTerm,
      }: {
        currentPage: number;
        numberOfCoursesToShow: number;
        searchTerm?: string;
      },
    ) {
      currentPage = currentPage || 0;
      const resultsPerPage = numberOfCoursesToShow || 200;
      const offset = resultsPerPage * currentPage;
      let response = null;

      if (searchTerm) {
        response = await query(
          `query searchCourseByName($searchTerm: String, $resultsPerPage: Int, $offset: Int){
            allCourses(filter: {
              name: {
                includesInsensitive: $searchTerm
              }
            }, orderBy: ID_DESC, first: $resultsPerPage, offset: $offset) {
              pageInfo {
                hasNextPage
                hasPreviousPage
              }
              nodes {
                id
                name
              }
            }
          }`,
          {
            searchTerm,
            resultsPerPage,
            offset,
          },
        );
      } else {
        response = await query(
          `query getAllCoursesDexAdmin($resultsPerPage: Int, $offset: Int){
          allCourses(first: $resultsPerPage, offset: $offset, orderBy: ID_DESC){
            pageInfo {
              hasNextPage
              hasPreviousPage
            }
            nodes {
              id
              name
            }
          }
        }`,
          {
            resultsPerPage,
            offset,
          },
        );
      }

      const coursesNodes = response.data.allCourses;
      if (coursesNodes) {
        commit('SET_COURSES', coursesNodes.nodes);
        commit('SET_COURSES_PAGINATION_INFORMATION', coursesNodes.pageInfo);
      }
    },

    async DELETE_COURSE_BY_ID({ dispatch }: any, course) {
      const deleteCourse = () =>
        query(
          `mutation deleteCourseByIdDexAdmin($courseId: Int!) {
          deleteCourseById(input: {id: $courseId}) {
            deletedCourseId
          }
        }`,
          {
            courseId: +course.id,
          },
        );

      if (course.hasAccessToPostgradStudyPlanning) {
        await Promise.all([
          dispatch('PUSH_POSTGRAD_STUDY_PLANNING_ACCESS', {
            course: course,
            hasAccessToPostgradStudyPlanning: false,
          }),
          deleteCourse(),
        ]);
      } else {
        await deleteCourse();
      }
    },
  },
};
